import { cssThemedValue, WoltLogo, Spinner } from '@creditornot/cb-ingredients';
import React from 'react';
import styled from 'styled-components';

import { PhotoShootStatus } from 'apis/mips/types';
import { useIsMobile, MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import useUser from 'common/hooks/useUser';

import CoreDetails from './CoreDetails';
import { PhotoShootStatusTag } from '../../components/PhotoShootStatusTag';
import UserDropdown from '../../components/UserDropdown';

type Props = {
  venueName?: string;
  expiresIn?: string;
  children: React.ReactNode;
  status?: PhotoShootStatus;
  isLoading?: boolean;
};

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 8px 24px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 12px;
    height: unset;
  }
`;

const DetailsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledWoltLogo = styled(WoltLogo)`
  height: 22px;
  fill: ${cssThemedValue('textPrimary')};
`;

const Content = styled.div`
  overflow: auto;
  flex: 1;
`;

const PrimaryDetailsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SpinnerContainer = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PhotoShootTemplate = ({ venueName, expiresIn, children, status, isLoading }: Props) => {
  const { user } = useUser();
  const isMobile = useIsMobile();

  return (
    <Root>
      <Header>
        <DetailsContainer>
          <StyledWoltLogo />
          {isLoading ? (
            <Spinner size="tiny" />
          ) : (
            <PrimaryDetailsContainer>
              {!isMobile && venueName && expiresIn && <CoreDetails venueName={venueName} expiresIn={expiresIn} />}

              {!!status && <PhotoShootStatusTag status={status} />}
              <UserDropdown user={user} />
            </PrimaryDetailsContainer>
          )}
        </DetailsContainer>
        {isMobile && venueName && expiresIn && <CoreDetails venueName={venueName} expiresIn={expiresIn} />}
      </Header>
      <Content>
        {isLoading ? (
          <SpinnerContainer>
            <Spinner size="large" />
          </SpinnerContainer>
        ) : (
          children
        )}
      </Content>
    </Root>
  );
};

export default PhotoShootTemplate;
